'use client';

import { Container, Grid, Typography } from '@mui/material';
import { Logo } from '../../assets/logo';
import { DevToolsUnlock } from '../../components/devToolsUnlock';
import { Footer } from '../../components/footer';
import { Header } from '../../components/header';
import { SafeSpace } from '../../components/safeSpace';
import { Sendpulse } from '../../components/sendpulse';
import { Transition } from '../../components/transition';
import { BRAND_GRADIENTS, BRAND_TONE, BrandTone } from '../../constants';

/**
 * Renders the main layout of the app, which includes a header, main content, and footer.
 *
 * @param {React.ReactNode} children - The content to be rendered within the layout.
 * @param {BrandTone} [tone] - Brand of the background wash, the app's NEXT_PUBLIC_BRAND_TONE by default.
 * @return {React.JSX.Element} The main layout of the app.
 */
export const OnbLayout = ({
  children,
  subtitle,
  tone = BRAND_TONE,
}: {
  children: React.ReactNode;
  subtitle?: string | React.ReactElement;
  tone?: BrandTone;
}): React.JSX.Element => {
  const showFooter = process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE !== 'onb-comercios';
  return (
    <Grid
      data-testid="app-layout"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: '100dvh',
        // Same brand wash as the MenuLayout app screens, fixed to the top of the window while the page scrolls
        background: BRAND_GRADIENTS.wash(tone),
        backgroundAttachment: 'fixed',
      }}
    >
      <Sendpulse id={process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-efectivo' ? 'onb_efectivo' : ''} />
      <Header />
      <Container
        component="main"
        id="main-content"
        sx={{
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: { xs: 'center', md: 'space-between' },

          gap: 4,
          background: {
            // xs: '#fff',
            xs: 'transparent',
          },
          px: { xs: 0, md: 4 },
          py: { xs: 0, md: 8 },
          flex: 1,
        }}
      >
        <Grid sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', pt: 6, gap: 4 }}>
          <Transition type="fadeLeft">
            <Grid sx={{ height: { md: 36, lg: 44 } }}>
              <DevToolsUnlock sx={{ height: '100%' }}>
                <Logo style={{ height: '100%' }} />
              </DevToolsUnlock>
            </Grid>
          </Transition>
          <Transition type="fadeLeft" delay={0.2}>
            {/* Marketing copy, not a heading: each step renders its own h1 */}
            <Typography component="div" variant="h1" sx={{ fontSize: { md: 28, lg: 34 }, lineHeight: 1.15 }}>
              {subtitle}
            </Typography>
          </Transition>
        </Grid>
        <SafeSpace>{children}</SafeSpace>
      </Container>
      <Footer showText={showFooter} />
    </Grid>
  );
};
